import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getCentralToday, getCentralMonth } from "@/utils/dateHelpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Bed, Plus, Trash2, Loader2, Calendar, Filter,
  FileCheck2, FileText, Archive, Download, CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BeddingTab({ rooms = [] }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterMonth, setFilterMonth] = useState(getCentralMonth());
  const [filterRoom, setFilterRoom] = useState("all");
  const [adding, setAdding] = useState(false);

  // Cycle state
  const [cycle, setCycle] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [closing, setClosing] = useState(false);

  // New entry form
  const [washRoom, setWashRoom] = useState("");
  const [washDate, setWashDate] = useState(getCentralToday());
  const [washNotes, setWashNotes] = useState("");

  const roomNumbers = rooms
    .map(r => r.room_number || r.roomNumber || r)
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/bedding-wash?month=${filterMonth}`;
      if (filterRoom && filterRoom !== "all") url += `&room_number=${filterRoom}`;
      const res = await axios.get(url);
      setLogs(res.data || []);
    } catch { toast.error("Failed to load bedding wash logs"); }
    finally { setLoading(false); }
  }, [filterMonth, filterRoom]);

  const fetchCycle = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        axios.get(`${API}/admin/bedding-wash/current-cycle`),
        axios.get(`${API}/admin/bedding-wash/cycles`),
      ]);
      setCycle(c.data);
      setCycles(h.data || []);
    } catch {
      // silent — feature is optional, logs tab still works
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchCycle(); }, [fetchCycle]);

  const handleAdd = async () => {
    if (!washRoom) { toast.error("Select a room"); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/bedding-wash`, {
        room_number: washRoom, date: washDate, notes: washNotes,
      });
      toast.success(`Bedding wash logged for Room ${washRoom}`);
      setWashNotes("");
      await Promise.all([fetchLogs(), fetchCycle()]);
    } catch { toast.error("Failed to add record"); }
    finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/bedding-wash/${id}`);
      toast.success("Record deleted");
      await Promise.all([fetchLogs(), fetchCycle()]);
    } catch { toast.error("Failed to delete"); }
  };

  const handleCloseCycle = async () => {
    if (!cycle?.is_complete) {
      toast.error(`Cannot close — ${cycle?.remaining_rooms?.length || 0} rooms still pending`);
      return;
    }
    if (!window.confirm(`Close this bedding wash cycle?\n\n${cycle.washed_count} of ${cycle.total_rooms} rooms washed. A PDF report will be archived and a new cycle will begin.`)) {
      return;
    }
    setClosing(true);
    try {
      const res = await axios.post(`${API}/admin/bedding-wash/close-cycle`);
      toast.success(`Cycle #${res.data.cycle_number} archived as PDF`);
      await fetchCycle();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close cycle");
    } finally {
      setClosing(false);
    }
  };

  const handleDownloadPdf = (cycleId, cycleNumber) => {
    const url = `${API}/admin/bedding-wash/cycles/${cycleId}/pdf`;
    window.open(url, "_blank");
  };

  // Room sets for overview
  const washedSet = new Set((cycle?.washed_rooms || []).map(r => String(r.room_number)));
  const progressPct = cycle?.total_rooms ? Math.round((cycle.washed_count / cycle.total_rooms) * 100) : 0;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-outfit text-xl text-vault-text flex items-center gap-2" data-testid="bedding-title">
          <Bed className="w-5 h-5 text-vault-gold" /> Bedding Wash Log
        </h2>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-vault-text-secondary" />
          <Input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
            className="w-44 bg-vault-surface border-vault-border text-vault-text" data-testid="bedding-filter-month" />
          <Filter className="w-4 h-4 text-vault-text-secondary ml-2" />
          <Select value={filterRoom} onValueChange={setFilterRoom}>
            <SelectTrigger className="w-32 bg-vault-surface border-vault-border text-vault-text" data-testid="bedding-filter-room">
              <SelectValue placeholder="All Rooms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Rooms</SelectItem>
              {roomNumbers.map(r => <SelectItem key={r} value={String(r)}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Current Cycle Status ─────────────────────────────────────── */}
      {cycle && (
        <Card className="bg-vault-surface border-vault-border mb-6" data-testid="bedding-cycle-status">
          <CardHeader className="border-b border-vault-border pb-3 flex flex-row items-center justify-between">
            <CardTitle className="font-outfit text-sm text-vault-text flex items-center gap-2">
              <FileCheck2 className="w-4 h-4 text-vault-gold" />
              Current Cycle
            </CardTitle>
            <Button
              size="sm"
              onClick={handleCloseCycle}
              disabled={!cycle.is_complete || closing}
              data-testid="bedding-close-cycle-btn"
              className={cycle.is_complete
                ? "bg-emerald-500 text-black hover:bg-emerald-400"
                : "bg-vault-surface-highlight text-vault-text-secondary cursor-not-allowed"}
            >
              {closing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
              {cycle.is_complete ? "Close Cycle & Generate PDF" : `${cycle.remaining_rooms?.length || 0} rooms to go`}
            </Button>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-center gap-4 mb-3">
              <div className="flex-1">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-vault-text-secondary text-xs">Progress</span>
                  <span className="text-vault-gold font-bold text-sm" data-testid="bedding-cycle-progress-label">
                    {cycle.washed_count} / {cycle.total_rooms} rooms &nbsp;·&nbsp; {progressPct}%
                  </span>
                </div>
                <div className="w-full h-2 rounded bg-black/40 overflow-hidden">
                  <div
                    className={`h-full transition-all ${cycle.is_complete ? "bg-emerald-400" : "bg-vault-gold"}`}
                    style={{ width: `${progressPct}%` }}
                    data-testid="bedding-cycle-progress-bar"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {roomNumbers.map(room => {
                const isWashed = washedSet.has(String(room));
                return (
                  <div
                    key={room}
                    data-testid={`bedding-cycle-room-${room}`}
                    className={`px-2.5 py-1 rounded text-xs font-mono border ${
                      isWashed
                        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                        : "bg-amber-500/10 border-amber-500/30 text-amber-300"
                    }`}
                    title={isWashed ? "Washed this cycle" : "Pending"}
                  >
                    {isWashed && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                    {room}
                  </div>
                );
              })}
            </div>
            {cycle.started_at && cycle.started_at !== "1970-01-01T00:00:00+00:00" && (
              <p className="text-vault-text-secondary text-[11px] mt-3">
                Cycle started {new Date(cycle.started_at).toLocaleDateString()}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Add Wash Entry ─────────────────────────────────────── */}
      <Card className="bg-vault-surface border-vault-border mb-6" data-testid="bedding-add-form">
        <CardHeader className="border-b border-vault-border pb-3">
          <CardTitle className="font-outfit text-sm text-vault-text">Log Bedding Wash</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-vault-text-secondary text-xs block mb-1">Room</label>
              <Select value={washRoom} onValueChange={setWashRoom}>
                <SelectTrigger className="w-28 bg-black/30 border-vault-border text-vault-text" data-testid="bedding-room-select">
                  <SelectValue placeholder="Room" />
                </SelectTrigger>
                <SelectContent>
                  {roomNumbers.map(r => <SelectItem key={r} value={String(r)}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-vault-text-secondary text-xs block mb-1">Date</label>
              <Input type="date" value={washDate} onChange={e => setWashDate(e.target.value)}
                className="w-40 bg-black/30 border-vault-border text-vault-text" data-testid="bedding-date" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-vault-text-secondary text-xs block mb-1">Notes</label>
              <Input value={washNotes} onChange={e => setWashNotes(e.target.value)} placeholder="e.g. Full set change, comforter replaced..."
                className="bg-black/30 border-vault-border text-vault-text" data-testid="bedding-notes" />
            </div>
            <Button onClick={handleAdd} disabled={adding || !washRoom} data-testid="bedding-add-btn"
              className="bg-vault-gold text-black hover:bg-vault-gold/90">
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Log Wash
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Cycle History (archived PDFs) ───────────────────────── */}
      {cycles.length > 0 && (
        <Card className="bg-vault-surface border-vault-border mb-6" data-testid="bedding-cycle-history">
          <CardHeader className="border-b border-vault-border pb-3">
            <CardTitle className="font-outfit text-sm text-vault-text flex items-center gap-2">
              <Archive className="w-4 h-4 text-vault-gold" /> Cycle History ({cycles.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="bedding-cycle-history-table">
                <thead className="bg-vault-surface-highlight border-b border-vault-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Cycle #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Date Range</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Rooms</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-vault-gold uppercase tracking-wider">PDF</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-vault-border">
                  {cycles.map(c => (
                    <tr key={c.id} className="hover:bg-vault-surface-highlight/50" data-testid={`bedding-cycle-row-${c.cycle_number}`}>
                      <td className="px-4 py-3 text-sm font-bold text-vault-gold">#{c.cycle_number}</td>
                      <td className="px-4 py-3 text-sm text-vault-text">
                        {c.started_at?.slice(0, 10)} → {c.closed_at?.slice(0, 10)}
                      </td>
                      <td className="px-4 py-3 text-sm text-vault-text-secondary">
                        {c.washed_count} / {c.total_rooms}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadPdf(c.id, c.cycle_number)}
                          className="text-vault-gold hover:text-vault-text"
                          data-testid={`bedding-download-pdf-${c.cycle_number}`}
                        >
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Wash History (monthly filtered) ─────────────────────────── */}
      <Card className="bg-vault-surface border-vault-border">
        <CardHeader className="border-b border-vault-border pb-3">
          <CardTitle className="font-outfit text-sm text-vault-text flex items-center gap-2">
            <FileText className="w-4 h-4 text-vault-gold" />
            Wash History — {filterMonth}{filterRoom !== "all" ? ` — Room ${filterRoom}` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-vault-gold" /></div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-vault-text-secondary text-sm">No bedding wash records for this period</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="bedding-log-table">
                <thead className="bg-vault-surface-highlight border-b border-vault-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Room</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Notes</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-vault-gold uppercase tracking-wider w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-vault-border">
                  {logs.map(log => (
                    <tr key={log.id} className="hover:bg-vault-surface-highlight/50" data-testid={`bedding-log-${log.id}`}>
                      <td className="px-4 py-3 text-sm text-vault-text">{log.date}</td>
                      <td className="px-4 py-3 text-sm font-medium text-vault-gold">{log.room_number}</td>
                      <td className="px-4 py-3 text-sm text-vault-text-secondary">{log.notes || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => handleDelete(log.id)} className="text-red-400 hover:text-red-300 p-1" data-testid={`bedding-delete-${log.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
